<template>
  <div class="eq-detail-page">
    <div class="eq-detail-header">
      <button class="btn-back" @click="$router.back()">
        <el-icon><ArrowLeft /></el-icon>
        <span>返回</span>
      </button>
      <div class="eq-detail-header-actions">
        <button class="btn-secondary" @click="editEq"><el-icon><Edit /></el-icon><span>编辑</span></button>
        <button class="btn-danger" @click="deleteEq"><el-icon><Delete /></el-icon><span>删除</span></button>
      </div>
    </div>

    <div v-if="loading" class="eq-detail-loading">
      <el-icon class="loading-icon"><Loading /></el-icon>
      <span>加载中...</span>
    </div>

    <div v-else-if="!equipment" class="eq-detail-empty">
      <el-icon><Warning /></el-icon>
      <p>器材信息不存在</p>
    </div>

    <div v-else class="eq-detail-body">
      <!-- 顶部 Hero：图片 + 基本信息 -->
      <div class="eq-detail-hero">
        <div class="eq-detail-img-wrap" :style="{background: iconBg}">
          <img v-if="imgUrl && !imgFailed"
               :src="imgUrl"
               class="eq-detail-img"
               :alt="equipment.name"
               @error="imgFailed = true" />
          <span v-else class="eq-detail-emoji">{{ emoji }}</span>
        </div>
        <div class="eq-detail-hero-info">
          <div class="eq-detail-name-row">
            <h1 class="eq-detail-name">{{ equipment.name }}</h1>
            <span class="eq-status-badge" :class="statusCls">{{ statusLabel }}</span>
          </div>
          <p class="eq-detail-model">{{ [equipment.brand, equipment.model].filter(Boolean).join(' · ') || '—' }}</p>
          <div class="eq-detail-tags">
            <span class="eq-tag"><el-icon><Location /></el-icon>{{ equipment.location || '暂未设置' }}</span>
            <span v-if="equipment.purchaseDate" class="eq-tag">
              <el-icon><Calendar /></el-icon>购买于 {{ equipment.purchaseDate }}
            </span>
            <span v-if="equipment.quantity != null" class="eq-tag eq-tag--blue">
              <el-icon><Goods /></el-icon>数量 {{ equipment.quantity }} 台
            </span>
            <span v-if="equipment.factoryPrice != null" class="eq-tag eq-tag--green">
              出厂价 ¥{{ equipment.factoryPrice }}
            </span>
          </div>
        </div>
      </div>

      <!-- 信息卡片区 -->
      <div class="eq-detail-cards">
        <!-- 基本信息 -->
        <div class="eq-info-card">
          <h3 class="eq-card-title">基本信息</h3>
          <div class="eq-info-grid">
            <div class="eq-info-item">
              <span class="eq-info-label">器材ID</span>
              <span class="eq-info-value"># {{ equipment.equipmentId }}</span>
            </div>
            <div class="eq-info-item">
              <span class="eq-info-label">器材名称</span>
              <span class="eq-info-value">{{ equipment.name }}</span>
            </div>
            <div class="eq-info-item">
              <span class="eq-info-label">品牌</span>
              <span class="eq-info-value">{{ equipment.brand || '—' }}</span>
            </div>
            <div class="eq-info-item">
              <span class="eq-info-label">型号</span>
              <span class="eq-info-value">{{ equipment.model || '—' }}</span>
            </div>
            <div class="eq-info-item">
              <span class="eq-info-label">存放位置</span>
              <span class="eq-info-value">{{ equipment.location || '—' }}</span>
            </div>
            <div class="eq-info-item">
              <span class="eq-info-label">使用状态</span>
              <span class="eq-status-badge" :class="statusCls">{{ statusLabel }}</span>
            </div>
            <div class="eq-info-item">
              <span class="eq-info-label">数量</span>
              <span class="eq-info-value">{{ equipment.quantity != null ? equipment.quantity + ' 台' : '—' }}</span>
            </div>
            <div class="eq-info-item">
              <span class="eq-info-label">出厂价</span>
              <span class="eq-info-value eq-price">{{ equipment.factoryPrice != null ? '¥' + equipment.factoryPrice : '—' }}</span>
            </div>
            <div class="eq-info-item eq-info-item--full">
              <span class="eq-info-label">图片路径</span>
              <span class="eq-info-value eq-mono">{{ equipment.imageUrl || '—' }}</span>
            </div>
            <div class="eq-info-item eq-info-item--full">
              <span class="eq-info-label">视频路径</span>
              <span class="eq-info-value eq-mono">{{ equipment.videoUrl || '—' }}</span>
            </div>
          </div>
        </div>

        <!-- 维护记录 -->
        <div class="eq-info-card">
          <h3 class="eq-card-title">维护记录</h3>
          <div class="eq-info-grid">
            <div class="eq-info-item">
              <span class="eq-info-label">购买日期</span>
              <span class="eq-info-value">{{ equipment.purchaseDate || '—' }}</span>
            </div>
            <div class="eq-info-item">
              <span class="eq-info-label">上次维护</span>
              <span class="eq-info-value">{{ equipment.lastMaintenance || '—' }}</span>
            </div>
            <div class="eq-info-item">
              <span class="eq-info-label">录入时间</span>
              <span class="eq-info-value">{{ formatDateTime(equipment.createTime) }}</span>
            </div>
            <div class="eq-info-item">
              <span class="eq-info-label">维护提示</span>
              <span class="eq-maint-hint" :class="maintHintCls">{{ maintHint }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 演示视频 -->
      <div v-if="videoPlayUrl" class="eq-info-card eq-video-card">
        <h3 class="eq-card-title">演示视频</h3>
        <video
          class="eq-video"
          :src="videoPlayUrl"
          controls
          playsinline
          preload="metadata"
        />
        <a :href="videoPlayUrl" class="eq-video-link" target="_blank" rel="noopener noreferrer">在新窗口打开</a>
      </div>

      <!-- 参数信息（整行展示） -->
      <div class="eq-info-card eq-specs-card">
        <h3 class="eq-card-title">参数信息</h3>
        <pre v-if="equipment.specifications" class="eq-specs-text">{{ equipment.specifications }}</pre>
        <p v-else class="eq-empty-line">暂无参数说明</p>
      </div>

      <!-- 描述（整行展示） -->
      <div class="eq-info-card eq-desc-card">
        <h3 class="eq-card-title">描述</h3>
        <p v-if="equipment.description" class="eq-desc-text">{{ equipment.description }}</p>
        <p v-else class="eq-empty-line">暂无描述</p>
      </div>
    </div>

    <!-- 编辑弹窗 -->
    <el-dialog v-model="showEditDialog" title="编辑器材" width="640px">
      <el-form :model="form" label-width="100px">
        <el-form-item label="器材名称"><el-input v-model="form.name" /></el-form-item>
        <el-form-item label="品牌"><el-input v-model="form.brand" /></el-form-item>
        <el-form-item label="型号"><el-input v-model="form.model" /></el-form-item>
        <el-form-item label="放置位置"><el-input v-model="form.location" /></el-form-item>
        <el-form-item label="购买日期">
          <el-date-picker v-model="form.purchaseDate" type="date" style="width:100%" value-format="YYYY-MM-DD" />
        </el-form-item>
        <el-form-item label="上次维护">
          <el-date-picker v-model="form.lastMaintenance" type="date" style="width:100%" value-format="YYYY-MM-DD" />
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="form.status" style="width:100%">
            <el-option label="正常" :value="1" />
            <el-option label="维修中" :value="0" />
            <el-option label="已报废" :value="2" />
          </el-select>
        </el-form-item>
        <el-form-item label="数量">
          <el-input-number v-model="form.quantity" :min="0" style="width:100%" />
        </el-form-item>
        <el-form-item label="出厂价（元）">
          <el-input-number v-model="form.factoryPrice" :min="0" :precision="2" style="width:100%" />
        </el-form-item>
        <el-form-item label="参数信息">
          <el-input v-model="form.specifications" type="textarea" :rows="4" placeholder="如：N.W/G.W: 150Kg/190Kg&#10;DIM: 1800*1710*800 mm" />
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="form.description" type="textarea" :rows="3" />
        </el-form-item>

        <el-form-item label="器材图片">
          <div class="upload-block">
            <div class="upload-preview upload-preview--image" v-if="form.imageUrl">
              <img :src="resolveMediaUrl(form.imageUrl)" :alt="form.name" />
            </div>
            <div class="upload-preview upload-preview--empty" v-else>
              <el-icon><Picture /></el-icon>
              <span>未上传图片</span>
            </div>
            <div class="upload-actions">
              <el-upload
                :show-file-list="false"
                accept="image/png,image/jpeg,image/gif,image/webp"
                :before-upload="beforeImageUpload"
                :http-request="uploadImage"
              >
                <button class="btn-secondary" type="button" :disabled="imageUploading">
                  <el-icon><Upload /></el-icon>
                  <span>{{ imageUploading ? '上传中…' : (form.imageUrl ? '更换图片' : '上传图片') }}</span>
                </button>
              </el-upload>
              <button v-if="form.imageUrl" class="btn-text-danger" type="button" @click="form.imageUrl = ''">
                清除
              </button>
            </div>
            <p class="upload-hint">支持 PNG / JPG / GIF / WebP，单张不超过 20MB</p>
          </div>
        </el-form-item>

        <el-form-item label="演示视频">
          <div class="upload-block">
            <div class="upload-preview upload-preview--video" v-if="form.videoUrl">
              <video :src="resolveMediaUrl(form.videoUrl)" controls preload="metadata" playsinline />
            </div>
            <div class="upload-preview upload-preview--empty" v-else>
              <el-icon><VideoCamera /></el-icon>
              <span>未上传视频</span>
            </div>
            <div class="upload-actions">
              <el-upload
                :show-file-list="false"
                accept="video/mp4,video/webm,video/quicktime,video/x-m4v"
                :before-upload="beforeVideoUpload"
                :http-request="uploadVideo"
              >
                <button class="btn-secondary" type="button" :disabled="videoUploading">
                  <el-icon><Upload /></el-icon>
                  <span>{{ videoUploading ? '上传中…' : (form.videoUrl ? '更换视频' : '上传视频') }}</span>
                </button>
              </el-upload>
              <button v-if="form.videoUrl" class="btn-text-danger" type="button" @click="form.videoUrl = ''">
                清除
              </button>
            </div>
            <p class="upload-hint">支持 MP4 / WebM / MOV / M4V，单个视频不超过 50MB</p>
          </div>
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <button class="btn-secondary" @click="showEditDialog=false">取消</button>
          <button class="btn-primary" @click="saveEq">保存</button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import {
  ArrowLeft, Edit, Delete, Location, Calendar, Loading, Warning, Goods,
  Picture, VideoCamera, Upload,
} from '@element-plus/icons-vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import api from '@/api/request';
import { resolveMediaUrl, equipmentCoverUrl } from '@/utils/media';

const MAX_IMAGE_BYTES = 20 * 1024 * 1024;
const MAX_VIDEO_BYTES = 50 * 1024 * 1024;

const emojis = { '跑步机': '🏃', '哑铃': '🏋️', '椭圆机': '🚴', '划船机': '🚣', '器材': '🔧' };
const statusMap = {
  0: { label: '维修中', cls: 'maint' },
  1: { label: '正常', cls: 'ok' },
  2: { label: '已报废', cls: 'off' },
};

export default {
  name: 'EquipmentDetailView',
  components: {
    ArrowLeft, Edit, Delete, Location, Calendar, Loading, Warning, Goods,
    Picture, VideoCamera, Upload,
  },
  data() {
    return {
      loading: true,
      equipment: null,
      imgFailed: false,
      showEditDialog: false,
      form: {},
      imageUploading: false,
      videoUploading: false,
    };
  },
  computed: {
    equipmentId() {
      return this.$route.params.id;
    },
    emoji() {
      if (!this.equipment) return '🔧';
      const key = Object.keys(emojis).find(k => this.equipment.name?.includes(k));
      return key ? emojis[key] : '🔧';
    },
    iconBg() {
      if (!this.equipment) return 'rgba(0,98,255,0.1)';
      return this.equipment.status === 1
        ? 'rgba(16,185,129,0.1)'
        : this.equipment.status === 0
          ? 'rgba(245,158,11,0.1)'
          : 'rgba(239,68,68,0.1)';
    },
    imgUrl() {
      if (!this.equipment) return '';
      return equipmentCoverUrl(this.equipment);
    },
    videoPlayUrl() {
      if (!this.equipment?.videoUrl) return '';
      return resolveMediaUrl(this.equipment.videoUrl);
    },
    statusLabel() {
      return statusMap[this.equipment?.status]?.label || '未知';
    },
    statusCls() {
      return statusMap[this.equipment?.status]?.cls || 'off';
    },
    maintHint() {
      if (!this.equipment?.lastMaintenance) return '暂无维护记录';
      const last = new Date(this.equipment.lastMaintenance);
      const now = new Date();
      const days = Math.floor((now - last) / (1000 * 60 * 60 * 24));
      if (days <= 30) return `距上次维护 ${days} 天（良好）`;
      if (days <= 90) return `距上次维护 ${days} 天（建议维护）`;
      return `距上次维护 ${days} 天（需要维护）`;
    },
    maintHintCls() {
      if (!this.equipment?.lastMaintenance) return 'hint-gray';
      const last = new Date(this.equipment.lastMaintenance);
      const now = new Date();
      const days = Math.floor((now - last) / (1000 * 60 * 60 * 24));
      if (days <= 30) return 'hint-green';
      if (days <= 90) return 'hint-yellow';
      return 'hint-red';
    },
  },
  mounted() {
    this.loadEquipment();
  },
  watch: {
    '$route.params.id'() {
      this.loadEquipment();
    },
  },
  methods: {
    async loadEquipment() {
      this.loading = true;
      this.imgFailed = false;
      try {
        const res = await api.getEquipmentInfo(this.equipmentId);
        if (res.data?.code === 200) {
          this.equipment = res.data.data;
        } else {
          this.equipment = null;
        }
      } catch {
        this.equipment = null;
      } finally {
        this.loading = false;
      }
    },
    formatDateTime(dt) {
      if (!dt) return '—';
      return String(dt).replace('T', ' ').substring(0, 19);
    },
    editEq() {
      this.form = {
        equipmentId: this.equipment.equipmentId,
        name: this.equipment.name,
        brand: this.equipment.brand,
        model: this.equipment.model,
        location: this.equipment.location,
        purchaseDate: this.equipment.purchaseDate,
        lastMaintenance: this.equipment.lastMaintenance,
        status: this.equipment.status,
        quantity: this.equipment.quantity,
        factoryPrice: this.equipment.factoryPrice != null ? Number(this.equipment.factoryPrice) : null,
        specifications: this.equipment.specifications,
        description: this.equipment.description,
        imageUrl: this.equipment.imageUrl || '',
        videoUrl: this.equipment.videoUrl || '',
      };
      this.showEditDialog = true;
    },
    async deleteEq() {
      try {
        await ElMessageBox.confirm(
          `确定删除器材「${this.equipment.name}」？此操作不可恢复。`,
          '删除确认',
          { type: 'warning' }
        );
        const res = await api.removeEquipment(this.equipment.equipmentId);
        if (res.data?.code === 200) {
          ElMessage.success('删除成功');
          this.$router.back();
        } else {
          ElMessage.error('删除失败');
        }
      } catch (e) {
        if (e !== 'cancel') ElMessage.error('删除失败');
      }
    },
    async saveEq() {
      try {
        const res = await api.updateEquipment({ ...this.form });
        if (res.data?.code === 200) {
          ElMessage.success('保存成功');
          this.showEditDialog = false;
          this.imgFailed = false;
          await this.loadEquipment();
        } else {
          ElMessage.error(res.data?.message || '保存失败');
        }
      } catch {
        ElMessage.error('保存失败');
      }
    },

    resolveMediaUrl,

    beforeImageUpload(file) {
      if (file.size > MAX_IMAGE_BYTES) {
        ElMessage.error('图片大小不能超过 20MB');
        return false;
      }
      return true;
    },
    beforeVideoUpload(file) {
      if (file.size > MAX_VIDEO_BYTES) {
        ElMessage.error('视频大小不能超过 50MB');
        return false;
      }
      return true;
    },
    async uploadImage({ file }) {
      this.imageUploading = true;
      try {
        const res = await api.uploadEquipmentImage(file);
        if (res.data?.code === 200 && res.data.data?.path) {
          this.form.imageUrl = res.data.data.path;
          ElMessage.success('图片上传成功');
        } else {
          ElMessage.error(res.data?.message || '图片上传失败');
        }
      } catch (e) {
        ElMessage.error('图片上传失败');
      } finally {
        this.imageUploading = false;
      }
    },
    async uploadVideo({ file }) {
      this.videoUploading = true;
      try {
        const res = await api.uploadEquipmentVideo(file);
        if (res.data?.code === 200 && res.data.data?.path) {
          this.form.videoUrl = res.data.data.path;
          ElMessage.success('视频上传成功');
        } else {
          ElMessage.error(res.data?.message || '视频上传失败');
        }
      } catch (e) {
        ElMessage.error('视频上传失败');
      } finally {
        this.videoUploading = false;
      }
    },
  },
};
</script>

<style scoped>
.eq-detail-page {
  padding: 0;
  min-height: 100vh;
  background: var(--bg-base, #f9fafb);
}

.eq-detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0 20px;
  flex-wrap: wrap;
  gap: 12px;
}

.btn-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 16px;
  height: 38px;
  background: var(--bg-white, #ffffff);
  color: var(--text-primary, #111827);
  border: 1px solid var(--border-color, #E5E7EB);
  border-radius: var(--radius-md, 8px);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-back:hover {
  border-color: var(--primary-blue, #0062FF);
  color: var(--primary-blue, #0062FF);
}

.eq-detail-header-actions {
  display: flex;
  gap: 10px;
}

.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 16px;
  height: 38px;
  background: var(--bg-gray, #F3F4F6);
  color: var(--text-primary, #111827);
  border: 1px solid var(--border-color, #E5E7EB);
  border-radius: var(--radius-md, 8px);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary:hover {
  border-color: var(--primary-blue, #0062FF);
  color: var(--primary-blue, #0062FF);
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 18px;
  height: 38px;
  background: var(--gradient-primary, linear-gradient(135deg, #0062FF 0%, #0036D9 100%));
  color: white;
  border: none;
  border-radius: var(--radius-md, 8px);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

.btn-danger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 16px;
  height: 38px;
  background: rgba(239, 68, 68, 0.08);
  color: #EF4444;
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: var(--radius-md, 8px);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-danger:hover {
  background: #EF4444;
  color: white;
}

.eq-detail-loading,
.eq-detail-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
  color: var(--text-muted, #6B7280);
  gap: 12px;
  font-size: 15px;
}

.loading-icon {
  font-size: 32px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Hero 区域 */
.eq-detail-hero {
  background: var(--bg-white, #ffffff);
  border: 1px solid var(--border-color, #E5E7EB);
  border-radius: var(--radius-lg, 12px);
  padding: 28px;
  display: flex;
  gap: 28px;
  align-items: flex-start;
  margin-bottom: 20px;
}

.eq-detail-img-wrap {
  width: 200px;
  height: 200px;
  border-radius: var(--radius-lg, 12px);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 64px;
  flex-shrink: 0;
  overflow: hidden;
  border: 1px solid var(--border-color, #E5E7EB);
}

.eq-detail-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #fff;
}

.eq-detail-emoji {
  font-size: 64px;
}

.eq-detail-hero-info {
  flex: 1;
  min-width: 0;
}

.eq-detail-name-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.eq-detail-name {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary, #111827);
  margin: 0;
}

.eq-detail-model {
  font-size: 14px;
  color: var(--text-muted, #6B7280);
  margin: 0 0 16px;
}

.eq-detail-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.eq-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  color: var(--text-secondary, #4B5563);
  background: var(--bg-gray, #F3F4F6);
  padding: 5px 12px;
  border-radius: 20px;
}

.eq-tag--blue {
  background: rgba(0, 98, 255, 0.08);
  color: #0062FF;
}

.eq-tag--green {
  background: rgba(16, 185, 129, 0.08);
  color: #10B981;
  font-weight: 600;
}

/* 状态徽章 */
.eq-status-badge {
  font-size: 12px;
  padding: 3px 10px;
  border-radius: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.eq-status-badge.ok { background: rgba(16,185,129,0.1); color: #10B981; }
.eq-status-badge.maint { background: rgba(245,158,11,0.1); color: #F59E0B; }
.eq-status-badge.off { background: rgba(239,68,68,0.1); color: #EF4444; }

/* 信息卡片 */
.eq-detail-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.eq-info-card {
  background: var(--bg-white, #ffffff);
  border: 1px solid var(--border-color, #E5E7EB);
  border-radius: var(--radius-lg, 12px);
  padding: 24px;
}

.eq-specs-card,
.eq-desc-card {
  margin-bottom: 20px;
}

.eq-card-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary, #111827);
  margin: 0 0 18px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--border-color, #E5E7EB);
}

.eq-info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.eq-info-item--full {
  grid-column: 1 / -1;
}

.eq-info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.eq-mono {
  font-family: ui-monospace, 'Consolas', monospace;
  font-size: 12px;
  word-break: break-all;
  line-height: 1.5;
  font-weight: 400;
}

.eq-info-label {
  font-size: 12px;
  color: var(--text-muted, #6B7280);
  font-weight: 500;
}

.eq-info-value {
  font-size: 14px;
  color: var(--text-primary, #111827);
  font-weight: 500;
}

.eq-price {
  font-size: 16px;
  font-weight: 700;
  color: #10B981;
}

/* 参数信息 */
.eq-specs-text {
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 13px;
  line-height: 1.8;
  color: var(--text-secondary, #4B5563);
  background: var(--bg-gray, #F9FAFB);
  border: 1px solid var(--border-color, #E5E7EB);
  border-radius: var(--radius-md, 8px);
  padding: 16px;
  white-space: pre-wrap;
  word-break: break-all;
  margin: 0;
}

.eq-desc-text {
  font-size: 14px;
  color: var(--text-secondary, #4B5563);
  line-height: 1.7;
  margin: 0;
}

.eq-empty-line {
  margin: 0;
  font-size: 14px;
  color: var(--text-muted, #9CA3AF);
}

.eq-video-card {
  margin-bottom: 20px;
}

.eq-video {
  display: block;
  width: 100%;
  max-height: 360px;
  border-radius: var(--radius-md, 8px);
  background: #000;
}

.eq-video-link {
  display: inline-block;
  margin-top: 12px;
  font-size: 13px;
  color: var(--primary-blue, #0062FF);
  text-decoration: none;
}

.eq-video-link:hover {
  text-decoration: underline;
}

/* 维护提示 */
.eq-maint-hint {
  font-size: 13px;
  font-weight: 500;
  padding: 2px 0;
}

.hint-green { color: #10B981; }
.hint-yellow { color: #F59E0B; }
.hint-red { color: #EF4444; }
.hint-gray { color: var(--text-muted, #6B7280); }

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* 上传区块 */
.upload-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.upload-preview {
  width: 100%;
  max-width: 360px;
  border: 1px solid var(--border-color, #E5E7EB);
  border-radius: var(--radius-md, 8px);
  overflow: hidden;
  background: var(--bg-gray, #F9FAFB);
}

.upload-preview--image img {
  width: 100%;
  max-height: 200px;
  object-fit: contain;
  display: block;
  background: #fff;
}

.upload-preview--video video {
  width: 100%;
  max-height: 220px;
  display: block;
  background: #000;
}

.upload-preview--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  color: var(--text-muted, #9CA3AF);
  font-size: 13px;
  border-style: dashed;
}

.upload-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.upload-actions .btn-secondary[disabled] {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-text-danger {
  background: transparent;
  border: none;
  color: #EF4444;
  cursor: pointer;
  font-size: 13px;
  padding: 4px 8px;
  border-radius: 4px;
}

.btn-text-danger:hover {
  background: rgba(239, 68, 68, 0.08);
}

.upload-hint {
  margin: 0;
  font-size: 12px;
  color: var(--text-muted, #9CA3AF);
}

/* 响应式 */
@media (max-width: 768px) {
  .eq-detail-hero {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .eq-detail-img-wrap {
    width: 140px;
    height: 140px;
  }

  .eq-detail-name-row {
    justify-content: center;
  }

  .eq-detail-tags {
    justify-content: center;
  }

  .eq-detail-cards {
    grid-template-columns: 1fr;
  }

  .eq-info-grid {
    grid-template-columns: 1fr;
  }
}
</style>
